// FinishSetupBanner — the way back into setup.
//
// WHY THIS EXISTS. Onboarding could be skipped or left part-finished, and there was no
// route back: `/onboarding` was added so skipping is not a one-way door, and then nothing
// in the entire app linked to it. A route with no entry point is the same trap in a
// different place, and it is the reason deferring anything to "later" was unsafe here --
// later was unreachable, so everything had to be crammed into the first run.
//
// This is what Jobber and HouseCall Pro both do, and it is the piece that was missing
// rather than the wizard itself: a guided setup at signup PLUS a persistent, visible way
// to finish it afterwards. With this in place, leaving setup early stops being a decision
// the user can regret.
//
// It reads onboarding_progress.completed_at directly instead of calling
// is_onboarding_complete(). That RPC answers "should the wizard be forced on this user",
// and returns true for a SKIPPED company as well as a finished one -- exactly the person
// who most needs this banner. Different question, different source.
//
// No dismiss button, deliberately. Dismissing would recreate the trap it exists to remove;
// it disappears on its own when setup is genuinely finished.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { useUser } from '../../contexts/UserContext';
import { ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/outline';

// The full flow; Quick Start covers a subset but the remaining work is the same work.
const TOTAL_STEPS = 6;

const FinishSetupBanner = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!user?.company_id) return;
      try {
        const { data, error } = await supabase
          .from('company_settings')
          .select('onboarding_progress')
          .eq('company_id', user.company_id)
          .maybeSingle();
        if (error || cancelled) return;

        const progress = data?.onboarding_progress || null;
        // No row yet means a company that has not started: still unfinished.
        if (progress?.completed_at) return;

        const done = Array.isArray(progress?.completed_steps) ? progress.completed_steps.length : 0;
        setState({ done });
      } catch {
        // A banner must never take the dashboard down with it.
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user?.company_id]);

  if (!state) return null;

  return (
    <div className="rounded-xl border border-blue-300/60 bg-blue-50 p-4 dark:border-blue-800/60 dark:bg-blue-950/30">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <SparklesIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Finish setting up your account</p>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {state.done > 0
                ? `${state.done} of ${TOTAL_STEPS} steps done. Pick up where you left off.`
                : 'Add your rates, payments and team whenever you are ready.'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/onboarding')}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
        >
          Continue setup
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default FinishSetupBanner;
